<script setup lang="ts">
import DSContainer from '~/components/ds/Container.vue'
import DSButtonLink from '~/components/ds/ButtonLink.vue'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const { fetchCurrentWeeklyDeal } = useWeeklyDealAPI()

const { data: weeklyDeal, error: weeklyDealError, refresh: refreshWeeklyDeal } = await useAsyncData(
  'current-weekly-deal',
  () => fetchCurrentWeeklyDeal(),
  { default: () => null },
)

const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  now.value = Date.now()
  timer = setInterval(() => { now.value = Date.now() }, 1000)
  // A transient SSR/API failure must not be cached as "there is no campaign".
  // Retry once in the browser; genuinely empty responses remain null.
  if (weeklyDealError.value) void refreshWeeklyDeal()
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})

function localized(values: Record<string, string> | null | undefined, fallback: string | null | undefined): string {
  return values?.[locale.value] || fallback || ''
}

const activeDeal = computed(() => {
  const deal = weeklyDeal.value
  if (!deal || now.value >= new Date(deal.endsAt).getTime()) return null
  return deal
})

const productPath = computed(() => {
  const deal = activeDeal.value
  if (!deal) return '/shop'
  const path = deal.productSlug ? `/producto/${deal.productSlug}` : `/producto/${deal.shopProductId}`
  return deal.inventoryItemId ? { path, query: { inventory: deal.inventoryItemId } } : path
})

const headline = computed(() => activeDeal.value
  ? localized(activeDeal.value.headlineTranslations, activeDeal.value.headline)
  : '')
const description = computed(() => {
  const deal = activeDeal.value
  if (!deal) return ''
  return localized(deal.subtitleTranslations, deal.subtitle)
    || localized(deal.productTitleTranslations, deal.productTitle)
})
const productTitle = computed(() => activeDeal.value
  ? localized(activeDeal.value.productTitleTranslations, activeDeal.value.productTitle)
  : '')
const dealImage = computed(() => activeDeal.value?.bannerImageUrl || activeDeal.value?.productImages?.[0] || null)
const imageIsBanner = computed(() => Boolean(activeDeal.value?.bannerImageUrl))

const discountPercent = computed(() => {
  const deal = activeDeal.value
  if (!deal) return 0
  const regular = Number(deal.regularPrice)
  return regular > 0 ? Math.max(0, Math.round(((regular - Number(deal.campaignPrice)) / regular) * 100)) : 0
})

function countdown(endsAt: string): string {
  const seconds = Math.max(0, Math.floor((new Date(endsAt).getTime() - now.value) / 1000))
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  const part = (unit: 'days' | 'hours' | 'minutes' | 'seconds', count: number) => t(`weeklyDeal.countdown.${unit}`, { count })
  return days > 0
    ? `${part('days', days)} ${part('hours', hours)} ${part('minutes', minutes)}`
    : `${part('hours', hours)} ${part('minutes', minutes)} ${part('seconds', secs)}`
}
</script>

<template>
  <section v-if="activeDeal" class="bg-gray-50 py-10 dark:bg-gray-950 sm:py-12" :aria-labelledby="'weekly-deal-title'">
    <DSContainer>
      <div class="overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-sm dark:border-amber-900/60 dark:bg-gray-900">
        <div class="grid items-stretch" :class="imageIsBanner ? '' : 'lg:grid-cols-[minmax(0,1fr)_minmax(24rem,0.9fr)]'">
          <div class="flex flex-col justify-center p-6 sm:p-8 lg:p-10">
            <div class="mb-4 inline-flex w-fit items-center gap-2 rounded-full bg-amber-100 px-3 py-1.5 text-sm font-semibold text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <UIcon name="i-lucide-flame" class="size-4" />
              {{ t('weeklyDeal.badge') }}
            </div>
            <h2 id="weekly-deal-title" class="text-3xl font-bold leading-tight text-gray-950 dark:text-white sm:text-4xl">
              {{ headline }}
            </h2>
            <p class="mt-3 max-w-2xl text-base leading-relaxed text-gray-600 dark:text-gray-300 sm:text-lg">
              {{ description }}
            </p>

            <div class="mt-6 flex flex-wrap items-end gap-x-3 gap-y-2 tabular-nums">
              <span class="text-4xl font-black text-brand-800 dark:text-brand-200 sm:text-5xl">€{{ Number(activeDeal.campaignPrice).toFixed(2) }}</span>
              <span class="pb-1 text-base text-gray-500 line-through dark:text-gray-400 sm:text-lg">€{{ Number(activeDeal.regularPrice).toFixed(2) }}</span>
              <span v-if="discountPercent > 0" class="mb-1 rounded-full bg-amber-300 px-2.5 py-1 text-xs font-bold text-amber-950">
                {{ t('weeklyDeal.discountOff', { percent: discountPercent }) }}
              </span>
            </div>

            <div class="mt-4 flex flex-wrap items-center gap-3 text-sm">
              <span class="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 font-semibold text-gray-700 tabular-nums dark:bg-gray-800 dark:text-gray-200">
                <UIcon name="i-lucide-clock-3" class="size-4" />
                {{ t('weeklyDeal.endsIn') }} {{ countdown(activeDeal.endsAt) }}
              </span>
              <span v-if="activeDeal.availableStock <= 5" class="font-semibold text-amber-800 dark:text-amber-300">
                {{ t('weeklyDeal.onlyLeft', { count: activeDeal.availableStock }) }}
              </span>
            </div>

            <div class="mt-7">
              <DSButtonLink :to="localePath(productPath)" size="lg" icon="i-lucide-zap">
                {{ t('weeklyDeal.cta') }}
              </DSButtonLink>
            </div>
          </div>

          <div v-if="dealImage" class="order-first flex items-center bg-gray-100 dark:bg-gray-800" :class="imageIsBanner ? '' : 'lg:order-last'">
            <img
              :src="imageUrl(dealImage, { w: imageIsBanner ? 1600 : 960, h: imageIsBanner ? 900 : 540, fit: 'contain' })"
              :srcset="`${imageUrl(dealImage, { w: 480, h: 270, fit: 'contain' })} 480w, ${imageUrl(dealImage, { w: 768, h: 432, fit: 'contain' })} 768w, ${imageUrl(dealImage, { w: 960, h: 540, fit: 'contain' })} 960w, ${imageUrl(dealImage, { w: 1600, h: 900, fit: 'contain' })} 1600w`"
              :sizes="imageIsBanner ? '(min-width: 1280px) 1280px, 100vw' : '(min-width: 1024px) 44vw, 100vw'"
              :alt="productTitle"
              width="960"
              height="540"
              loading="lazy"
              class="aspect-video w-full object-contain"
              :class="imageIsBanner ? '' : 'p-6'"
            >
          </div>
        </div>
      </div>
    </DSContainer>
  </section>
</template>
